import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, humanBytes, formatApiError } from "../lib/api";
import {
    Search,
    Download,
    FolderKanban,
    Image as ImageIcon,
    Video as VideoIcon,
    FileType2,
    FileText,
    FileQuestion,
    Loader2,
    Sparkles,
    X,
    RefreshCw,
    LayoutGrid,
    List as ListIcon,
    ArrowUpDown,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from "../components/ui/dialog";
import { Toaster, toast } from "sonner";
import FileComments from "../components/FileComments";

const KIND_ICONS = {
    image: ImageIcon,
    video: VideoIcon,
    pdf: FileType2,
    text: FileText,
    other: FileQuestion,
};

const PAGE_SIZE = 20;
// Public client (no auth header)
const pub = axios.create({ baseURL: API });

export default function SharedProjectPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [files, setFiles] = useState(null);
    const [total, setTotal] = useState(0);
    const [nextOffset, setNextOffset] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState(() => localStorage.getItem("sf_share_sort") || "name_asc");
    const [view, setView] = useState(() => localStorage.getItem("sf_share_view") || "grid");
    const [selectedId, setSelectedId] = useState(null);
    const [downloadingZip, setDownloadingZip] = useState(false);
    const [zipProgress, setZipProgress] = useState(null);
    const debounceRef = useRef(null);

    const loadInfo = useCallback(async () => {
        try {
            const { data } = await pub.get(`/share/${token}`);
            setInfo(data);
        } catch (e) {
            setError(formatApiError(e));
        }
    }, [token]);

    const loadFiles = useCallback(
        async (query = "") => {
            try {
                const { data } = await pub.get(`/share/${token}/files`, {
                    params: { limit: PAGE_SIZE, q: query, sort, offset: 0 },
                });
                setFiles(data.items || []);
                setTotal(data.total || 0);
                setNextOffset(data.next_offset ?? null);
            } catch (e) {
                setError(formatApiError(e));
            }
        },
        [token, sort]
    );

    useEffect(() => {
        loadInfo();
    }, [loadInfo]);

    // Debounced search
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            loadFiles(q);
        }, 250);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [q, loadFiles]);

    const loadMore = async () => {
        if (nextOffset == null || loadingMore) return;
        setLoadingMore(true);
        try {
            // "Alles tonen": blijf pagina's ophalen tot alles geladen is
            let offset = nextOffset;
            let all = [...(files || [])];
            while (offset != null) {
                // eslint-disable-next-line no-await-in-loop
                const { data } = await pub.get(`/share/${token}/files`, {
                    params: { limit: 200, sort, offset, q },
                });
                all = [...all, ...(data.items || [])];
                offset = data.next_offset ?? null;
                setFiles([...all]);
                setNextOffset(offset);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoadingMore(false);
        }
    };

    const changeSort = (s) => {
        setSort(s);
        localStorage.setItem("sf_share_sort", s);
    };

    const changeView = (v) => {
        setView(v);
        localStorage.setItem("sf_share_view", v);
    };

    const downloadAll = async () => {
        if (downloadingZip) return;
        setDownloadingZip(true);
        setZipProgress(null);
        try {
            // WeTransfer-stijl: ZIP wordt eerst in de opslag klaargezet,
            // daarna volgt een directe (hervatbare) downloadlink.
            let { data: job } = await pub.post(`/share/${token}/zip`);
            let pollErrors = 0;
            const started = Date.now();
            while (job.status === "building") {
                setZipProgress({ done: job.done_bytes, total: job.total_bytes });
                if (Date.now() - started > 60 * 60 * 1000) {
                    throw new Error("ZIP voorbereiden duurt te lang — probeer het opnieuw.");
                }
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 2500));
                try {
                    // eslint-disable-next-line no-await-in-loop
                    ({ data: job } = await pub.get(`/share/${token}/zip/${job.job_id}`));
                    pollErrors = 0;
                } catch (e) {
                    pollErrors += 1;
                    if (pollErrors >= 8) throw e;
                }
            }
            if (job.status === "ready" && job.download_url) {
                const a = document.createElement("a");
                a.href = job.download_url;
                a.rel = "noopener";
                document.body.appendChild(a);
                a.click();
                a.remove();
                toast.success("Download gestart — rechtstreeks uit de opslag, hervatbaar bij onderbreking.");
            } else {
                throw new Error(job.error || "ZIP maken mislukt — probeer het opnieuw.");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDownloadingZip(false);
            setZipProgress(null);
        }
    };

    if (error) {
        return (
            <ErrorScreen message={error} />
        );
    }
    if (!info) {
        return (
            <div className="min-h-screen flex items-center justify-center text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin" />
            </div>
        );
    }

    const { project, customer, file_count, total_size_bytes } = info;

    return (
        <div className="min-h-screen bg-[#f4f4f5]">
            {/* Header */}
            <header className="bg-white border-b border-zinc-200 sticky top-0 z-20">
                <div className="px-4 md:px-10 py-4 max-w-[1500px] mx-auto flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        {customer.logo_url ? (
                            <img
                                src={customer.logo_url}
                                alt={customer.name}
                                className="w-10 h-10 rounded-sm border border-zinc-200 object-contain bg-white"
                            />
                        ) : (
                            <div className="w-10 h-10 bg-[#09090b] text-white rounded-sm flex items-center justify-center font-display font-black">
                                {(customer.name || "?").slice(0, 1).toUpperCase()}
                            </div>
                        )}
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                                Gedeeld via Social Factory Portal
                            </div>
                            <div className="font-display text-lg leading-none tracking-tight font-bold truncate">
                                {customer.name}
                            </div>
                        </div>
                    </div>
                    <Button
                        onClick={downloadAll}
                        disabled={downloadingZip || file_count === 0}
                        className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                        data-testid="share-download-zip"
                    >
                        {downloadingZip ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                <span data-testid="zip-progress-label">
                                    {zipProgress && zipProgress.total > 0
                                        ? `ZIP voorbereiden… ${Math.min(100, Math.round((zipProgress.done / zipProgress.total) * 100))}%`
                                        : "ZIP voorbereiden…"}
                                </span>
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4 mr-2" />
                                <span className="hidden sm:inline">Alles downloaden ({humanBytes(total_size_bytes)})</span>
                                <span className="sm:hidden">ZIP</span>
                            </>
                        )}
                    </Button>
                </div>
            </header>

            <main className="px-4 md:px-10 py-6 md:py-10 max-w-[1500px] mx-auto" data-testid="share-page">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                    Project
                </div>
                <h1 className="font-display text-3xl md:text-5xl tracking-tighter font-black leading-none break-words">
                    {project.name}
                </h1>
                {project.description && (
                    <p className="text-sm text-zinc-600 mt-3 max-w-xl">
                        {project.description}
                    </p>
                )}
                <div className="text-xs font-mono text-zinc-500 mt-3">
                    {file_count} bestand{file_count === 1 ? "" : "en"} · {humanBytes(total_size_bytes)}
                </div>

                {/* Search + sort + view toggle */}
                <div className="flex items-center gap-2 mt-6 md:mt-8 mb-6 flex-wrap">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Zoek in beschrijvingen, tags of bestandsnamen…"
                            className="w-full bg-white border border-zinc-300 rounded-md pl-12 pr-4 py-3 text-base shadow-sm h-auto"
                            data-testid="share-search"
                        />
                    </div>
                    <Select value={sort} onValueChange={changeSort}>
                        <SelectTrigger
                            className="w-auto min-w-[170px] bg-white border-zinc-300 rounded-md h-[50px] shadow-sm"
                            data-testid="share-sort-select"
                        >
                            <ArrowUpDown className="w-4 h-4 text-zinc-500 mr-1.5 shrink-0" />
                            <SelectValue placeholder="Sorteren" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="name_asc" data-testid="share-sort-name-asc">Naam A → Z</SelectItem>
                            <SelectItem value="name_desc" data-testid="share-sort-name-desc">Naam Z → A</SelectItem>
                            <SelectItem value="created_desc" data-testid="share-sort-created-desc">Nieuwste eerst</SelectItem>
                            <SelectItem value="created_asc" data-testid="share-sort-created-asc">Oudste eerst</SelectItem>
                            <SelectItem value="size_desc" data-testid="share-sort-size-desc">Grootste eerst</SelectItem>
                            <SelectItem value="size_asc" data-testid="share-sort-size-asc">Kleinste eerst</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="flex items-center bg-white border border-zinc-300 rounded-md shadow-sm h-[50px] p-1">
                        <button
                            type="button"
                            onClick={() => changeView("grid")}
                            title="Rasterweergave"
                            className={`h-full px-3 rounded-[4px] flex items-center justify-center transition-colors ${
                                view === "grid" ? "bg-[#0044FF] text-white" : "text-zinc-500 hover:text-zinc-900"
                            }`}
                            data-testid="share-view-grid-btn"
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => changeView("list")}
                            title="Lijstweergave"
                            className={`h-full px-3 rounded-[4px] flex items-center justify-center transition-colors ${
                                view === "list" ? "bg-[#0044FF] text-white" : "text-zinc-500 hover:text-zinc-900"
                            }`}
                            data-testid="share-view-list-btn"
                        >
                            <ListIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {!files ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="aspect-square bg-white border border-zinc-200 rounded-md animate-pulse" />
                        ))}
                    </div>
                ) : files.length === 0 ? (
                    <div className="border border-dashed border-zinc-300 rounded-md py-16 text-center bg-white">
                        <FolderKanban className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
                        <div className="font-display text-xl tracking-tight mb-2">
                            {q ? "Geen resultaten" : "Geen bestanden"}
                        </div>
                        <p className="text-sm text-zinc-500">
                            {q ? "Probeer andere zoektermen." : "Dit gedeelde project bevat nog geen bestanden."}
                        </p>
                    </div>
                ) : (
                    <>
                        {view === "grid" ? (
                            <div
                                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4"
                                data-testid="share-file-grid"
                            >
                                {files.map((f) => (
                                    <SharedFileCard
                                        key={f.id}
                                        file={f}
                                        onClick={() => setSelectedId(f.id)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div
                                className="bg-white border border-zinc-200 rounded-md overflow-hidden divide-y divide-zinc-100"
                                data-testid="share-file-list"
                            >
                                <div className="hidden md:grid grid-cols-[56px_1fr_110px_150px_60px] gap-3 items-center px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                                    <span />
                                    <span>Naam</span>
                                    <span>Grootte</span>
                                    <span>Datum</span>
                                    <span className="text-right">Acties</span>
                                </div>
                                {files.map((f) => (
                                    <SharedFileRow
                                        key={f.id}
                                        file={f}
                                        onClick={() => setSelectedId(f.id)}
                                    />
                                ))}
                            </div>
                        )}
                        {nextOffset != null && (
                            <div className="flex flex-col items-center mt-8 gap-2">
                                <div className="text-xs font-mono text-zinc-500">
                                    {files.length} van {total} getoond
                                </div>
                                <Button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    variant="outline"
                                    className="rounded-md"
                                    data-testid="share-load-more"
                                >
                                    {loadingMore ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Alles laden…
                                        </>
                                    ) : (
                                        <>Alles tonen ({total - files.length} meer)</>
                                    )}
                                </Button>
                            </div>
                        )}
                    </>
                )}

                <footer className="mt-16 pt-8 border-t border-zinc-200 text-xs text-zinc-500 text-center">
                    Gedeeld via{" "}
                    <a
                        href="https://portal.social-factory.be"
                        className="text-[#0044FF] hover:underline"
                    >
                        Social Factory AI Portal
                    </a>
                </footer>
            </main>

            {selectedId && (
                <SharedFileDialog
                    token={token}
                    fileId={selectedId}
                    onClose={() => setSelectedId(null)}
                />
            )}

            <Toaster richColors position="top-right" />
        </div>
    );
}

function SharedFileCard({ file, onClick }) {
    const Icon = KIND_ICONS[file.kind] || FileQuestion;
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => e.key === "Enter" && onClick?.()}
            className="group relative bg-white border border-zinc-200 rounded-md overflow-hidden cursor-pointer hover:border-[#0044FF] hover:shadow-md transition-all text-left flex flex-col"
            data-testid={`share-file-${file.id}`}
        >
            <div className="aspect-square bg-zinc-50 relative overflow-hidden flex items-center justify-center">
                {file.thumb_url ? (
                    <img
                        src={file.thumb_url}
                        alt={file.original_name}
                        className="w-full h-full object-contain bg-zinc-50"
                        loading="lazy"
                        decoding="async"
                    />
                ) : (
                    <div className="flex flex-col items-center gap-2 text-zinc-400">
                        <Icon className="w-12 h-12" />
                        <span className="text-[10px] font-mono uppercase">{file.kind}</span>
                    </div>
                )}
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/75 to-transparent text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <div className="text-xs font-mono line-clamp-3">
                        {file.ai_description || file.original_name}
                    </div>
                </div>
                {file.download_url && (
                    <a
                        href={file.download_url}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute bottom-2 right-2 z-10 w-8 h-8 rounded-md bg-black/55 hover:bg-[#0044FF] text-white flex items-center justify-center backdrop-blur-sm transition-colors opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                        title="Download origineel"
                        aria-label={`Download ${file.original_name}`}
                        data-testid={`share-file-download-${file.id}`}
                    >
                        <Download className="w-4 h-4" />
                    </a>
                )}
            </div>
            <div className="p-2.5 md:p-3">
                <div className="text-sm font-medium truncate">{file.original_name}</div>
                <div className="flex items-center justify-between mt-1">
                    <span className="text-xs font-mono text-zinc-500">
                        {humanBytes(file.size_bytes)}
                    </span>
                    {file.ai_tags?.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-[#0044FF]">
                            <Sparkles className="w-3 h-3" />
                            AI
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function SharedFileRow({ file, onClick }) {
    const Icon = KIND_ICONS[file.kind] || FileQuestion;
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => e.key === "Enter" && onClick?.()}
            className="grid grid-cols-[56px_1fr_60px] md:grid-cols-[56px_1fr_110px_150px_60px] gap-3 items-center px-3 py-2 cursor-pointer hover:bg-zinc-50 transition-colors"
            data-testid={`share-file-row-${file.id}`}
        >
            <div className="w-14 h-14 rounded-sm bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center shrink-0">
                {file.thumb_url ? (
                    <img
                        src={file.thumb_url}
                        alt={file.original_name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                    />
                ) : (
                    <Icon className="w-6 h-6 text-zinc-400" />
                )}
            </div>
            <div className="min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm font-medium truncate">{file.original_name}</span>
                    {file.ai_tags?.length > 0 && (
                        <Sparkles className="w-3 h-3 text-[#0044FF] shrink-0" />
                    )}
                </div>
                {file.ai_description && (
                    <div className="text-xs text-zinc-500 truncate mt-0.5">{file.ai_description}</div>
                )}
                <div className="text-[11px] font-mono text-zinc-500 md:hidden mt-0.5">
                    {humanBytes(file.size_bytes)}
                </div>
            </div>
            <span className="hidden md:block text-xs font-mono text-zinc-600">
                {humanBytes(file.size_bytes)}
            </span>
            <span className="hidden md:block text-xs font-mono text-zinc-600">
                {file.created_at
                    ? new Date(file.created_at).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" })
                    : "—"}
            </span>
            <div className="flex items-center justify-end">
                {file.download_url && (
                    <a
                        href={file.download_url}
                        onClick={(e) => e.stopPropagation()}
                        className="w-8 h-8 rounded-md text-zinc-500 hover:text-white hover:bg-[#0044FF] flex items-center justify-center transition-colors"
                        title="Download origineel"
                        data-testid={`share-file-row-download-${file.id}`}
                    >
                        <Download className="w-4 h-4" />
                    </a>
                )}
            </div>
        </div>
    );
}

function SharedFileDialog({ token, fileId, onClose }) {
    const [file, setFile] = useState(null);
    const [error, setError] = useState("");
    const videoRef = useRef(null);
    useEffect(() => {
        pub.get(`/share/${token}/files/${fileId}`)
            .then((r) => setFile(r.data))
            .catch((e) => setError(formatApiError(e)));
    }, [token, fileId]);

    const Icon = file ? KIND_ICONS[file.kind] || FileQuestion : FileQuestion;

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-4xl w-[calc(100%-1rem)] max-h-[90vh] overflow-y-auto p-4 md:p-6"
                data-testid="share-file-dialog"
            >
                <DialogTitle className="sr-only">Bestand</DialogTitle>
                {error && <div className="text-red-600 text-sm">{error}</div>}
                {!file ? (
                    <div className="py-20 flex justify-center text-zinc-500">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        {file.kind === "video" && file.preview_url ? (
                            <video
                                ref={videoRef}
                                src={file.preview_url}
                                controls
                                playsInline
                                className="w-full max-h-[70vh] bg-black rounded-md border border-zinc-200"
                                data-testid="share-file-video"
                            />
                        ) : (
                            <div className="bg-zinc-100 border border-zinc-200 rounded-md aspect-square flex items-center justify-center overflow-hidden">
                                {file.kind === "image" && file.preview_url ? (
                                    <img
                                        src={file.preview_url}
                                        alt={file.original_name}
                                        className="w-full h-full object-contain bg-zinc-50"
                                    />
                                ) : file.kind === "pdf" && file.preview_url ? (
                                    <iframe
                                        src={file.preview_url}
                                        title={file.original_name}
                                        className="w-full h-full bg-white"
                                    />
                                ) : (
                                    <div className="flex flex-col items-center gap-3 text-zinc-400">
                                        <Icon className="w-16 h-16" />
                                        <span className="text-xs font-mono uppercase">{file.kind}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex flex-col gap-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                                    Bestand
                                </div>
                                <h2 className="font-display text-2xl tracking-tight font-bold leading-tight break-all">
                                    {file.original_name}
                                </h2>
                                <div className="text-xs font-mono text-zinc-500 mt-2">
                                    {file.mime_type} · {humanBytes(file.size_bytes)} ·{" "}
                                    {new Date(file.created_at).toLocaleString("nl-BE")}
                                </div>
                            </div>

                            <div className="border-t border-zinc-200 pt-4">
                                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">
                                    <Sparkles className="w-3 h-3" /> AI Beschrijving
                                </div>
                                {file.ai_description ? (
                                    <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">
                                        {file.ai_description}
                                    </p>
                                ) : (
                                    <div className="text-sm text-zinc-500">
                                        Geen beschrijving beschikbaar.
                                    </div>
                                )}
                                {file.ai_tags?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-4">
                                        {file.ai_tags.map((t) => (
                                            <span
                                                key={t}
                                                className="inline-flex items-center px-2 py-1 rounded-sm bg-zinc-100 border border-zinc-200 text-xs font-mono text-zinc-700"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="mt-auto pt-4 border-t border-zinc-200">
                                <a
                                    href={file.download_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0044FF] hover:bg-[#0033CC] text-white rounded-md text-sm font-medium"
                                    data-testid="share-file-download"
                                >
                                    <Download className="w-4 h-4" />
                                    Download
                                </a>
                            </div>

                            {/* Comments — public */}
                            <FileComments fileId={file.id} mode="share" shareToken={token} videoRef={videoRef} />
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function ErrorScreen({ message }) {
    return (
        <div className="min-h-screen bg-[#f4f4f5] flex items-center justify-center px-4">
            <div className="bg-white border border-zinc-200 rounded-md p-8 max-w-md text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4">
                    <X className="w-6 h-6" />
                </div>
                <h1 className="font-display text-2xl tracking-tight font-bold mb-2">
                    Link niet beschikbaar
                </h1>
                <p className="text-sm text-zinc-600 mb-4">{message}</p>
                <a
                    href="https://portal.social-factory.be"
                    className="inline-flex items-center gap-1.5 text-sm text-[#0044FF] hover:underline"
                >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Naar Social Factory Portal
                </a>
            </div>
        </div>
    );
}
